import { extractCreatorId } from './authUtils';
import { normalizeBackendOrderStatus } from './teamSalesOrderEligibility';

const COMPLETED = new Set(['delivered', 'completed', 'complete']);
const CANCELLED = new Set(['cancelled', 'canceled']);
const PENDING_ONLY = new Set(['pending', 'order placed']);

/** Matches Orders.js display labels for breakdown rows */
const STATUS_DISPLAY = {
  pending: 'Order Placed',
  confirmed: 'Order Confirmed',
  processing: 'Processing',
  packed: 'Packed',
  dispatched: 'Dispatched',
  shipped: 'Dispatched',
  'in transit': 'In Transit',
  in_transit: 'In Transit',
  'out for delivery': 'Out for Delivery',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  canceled: 'Cancelled',
  completed: 'Delivered',
  complete: 'Delivered',
};

function normalizeStatusSlug(order) {
  let s = normalizeBackendOrderStatus(order);
  if (s.startsWith('order ')) s = s.slice(6).trim();
  return s || 'pending';
}

export function displayOrderStatusLabel(rawStatus) {
  const s = normalizeStatusSlug({ status: rawStatus });
  if (!s || s === 'pending') return 'Order Placed';
  return STATUS_DISPLAY[s] || rawStatus || 'Order Placed';
}

export function filterOrdersForAnalyticsScope(orders, { isTeamMember, myId } = {}) {
  const list = Array.isArray(orders) ? orders : [];
  if (!isTeamMember || !myId) return list;
  return list.filter((order) => {
    const items = order?.items || [];
    return items.some((item) => {
      const product = item.product || item.productId;
      if (!product || typeof product !== 'object') return false;
      return extractCreatorId(product.createdBy) === myId;
    });
  });
}

/**
 * Order overview + status breakdown from GET /admin/orders (same source as Sales).
 * Buckets: completed (delivered), pending (placed/pending + in-fulfillment pipeline), cancelled.
 */
export function aggregateOrderStatsFromOrders(orders) {
  const list = Array.isArray(orders) ? orders : [];
  const statusCounts = {};
  let completed = 0;
  let pending = 0;
  let cancelled = 0;

  for (const order of list) {
    const s = normalizeStatusSlug(order);

    const label = displayOrderStatusLabel(order?.status ?? s);
    statusCounts[label] = (statusCounts[label] || 0) + 1;

    if (CANCELLED.has(s)) {
      cancelled += 1;
    } else if (COMPLETED.has(s)) {
      completed += 1;
    } else if (PENDING_ONLY.has(s)) {
      pending += 1;
    } else {
      // Confirmed, processing, shipped, etc. — active orders still in pipeline
      pending += 1;
    }
  }

  return {
    total: list.length,
    completed,
    pending,
    cancelled,
    statusCounts,
  };
}

/** Sum numeric values from analytics API statusCounts with flexible keys */
export function pickAnalyticsStatusCounts(statusCounts) {
  const sc = statusCounts && typeof statusCounts === 'object' ? statusCounts : {};
  const get = (...keys) => {
    for (const k of keys) {
      if (sc[k] != null && sc[k] !== '') return Number(sc[k]) || 0;
    }
    const lower = Object.create(null);
    for (const [key, val] of Object.entries(sc)) {
      lower[String(key).toLowerCase()] = val;
    }
    for (const k of keys) {
      const v = lower[String(k).toLowerCase()];
      if (v != null && v !== '') return Number(v) || 0;
    }
    return 0;
  };

  return {
    completed: get('delivered', 'Delivered', 'completed', 'Completed', 'complete'),
    pending: get(
      'pending',
      'Pending',
      'order placed',
      'Order Placed',
      'processing',
      'Processing',
      'confirmed',
      'Order Confirmed'
    ),
    cancelled: get('cancelled', 'Cancelled', 'canceled', 'Canceled'),
  };
}
